import { useListenPublishedCheckoutPageQuery } from 'modules/PageBuilder/pageBuilderApi'
import { useListenTabContentQuery } from 'modules/Layout/layoutApi'
import { MIN_PRODUCT_PRICE } from 'modules/ProductInfo/components/ProductForm/schema'

const isQueryLoading = (data) => data === undefined || data?.isLoading
const getLoadedData = (data) => (isQueryLoading(data) ? null : data)

// Legacy products predate the readyForSale flag: when it was never set,
// fall back to the old rule — live once the checkout page is published.
// Only an explicit false hides a product, explicit true shows it.
export function getIsProductReadyForSale({ product, publishedCheckoutPage }) {
  if (product?.readyForSale == null) {
    return Boolean(publishedCheckoutPage)
  }
  return Boolean(product.readyForSale)
}

export function getIsContentAdded({ item, tabContent }) {
  if (!item?.type) {
    return false
  }
  if (item.type === 'tab') {
    return Object.values(tabContent || {}).some((row) => Object.values(row?.items || {}).length > 0)
  }
  return Boolean(item?.id)
}

export function getReadyForSaleTasks({ product, tabContent, publishedCheckoutPage }) {
  return [
    { id: 'name', label: 'Add name', isComplete: Boolean(product?.stripeProductName) },
    { id: 'link', label: 'Set up link', isComplete: Boolean(product?.slug) },
    { id: 'content', label: 'Add content', isComplete: getIsContentAdded({ item: product?.item, tabContent }) },
    { id: 'price', label: 'Set price', isComplete: Number(product?.price) >= MIN_PRODUCT_PRICE },
    { id: 'checkoutPage', label: 'Publish checkout page', isComplete: Boolean(publishedCheckoutPage) },
  ]
}

// Tasks are computed from the saved product data, so unsaved form edits
// don't count towards completion until the coach hits Save
export function useReadyForSaleTasks({ coachOrgId, productId, product, skip = false }) {
  const item = product?.item
  const isTabProduct = item?.type === 'tab'

  const { data: publishedCheckoutPageData } = useListenPublishedCheckoutPageQuery({ coachOrgId, productId }, { skip })
  const { data: tabContentData } = useListenTabContentQuery(
    { coachOrgId, tabId: item?.id },
    { skip: skip || !isTabProduct || !item?.id }
  )

  const isLoading =
    skip ||
    isQueryLoading(publishedCheckoutPageData) ||
    (isTabProduct && Boolean(item?.id) && isQueryLoading(tabContentData))

  const tasks = getReadyForSaleTasks({
    product,
    tabContent: getLoadedData(tabContentData),
    publishedCheckoutPage: getLoadedData(publishedCheckoutPageData),
  })
  const incompleteTasks = tasks.filter((task) => !task.isComplete)

  return {
    tasks,
    incompleteTasks,
    allTasksComplete: incompleteTasks.length === 0,
    isReadyForSale: getIsProductReadyForSale({
      product,
      publishedCheckoutPage: getLoadedData(publishedCheckoutPageData),
    }),
    isLoading,
  }
}
